import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { X, ArrowRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useLanguage } from "@/contexts/LanguageContext";
import ContactConciergeModal from "@/components/ContactConciergeModal";

interface RenovationRibbonProps {
  translationBase: string;
  storageKey: string;
  gradientClassName?: string;
  ringOffsetClassName?: string;
  dataOid?: string;
  /** When set, shows the primary CTA as a link (e.g. Book your stay). */
  primaryHref?: string;
}

const RenovationRibbon: React.FC<RenovationRibbonProps> = ({
  translationBase,
  storageKey,
  gradientClassName = "from-alpine-gold via-alpine-gold/95 to-alpine-gold",
  ringOffsetClassName = "focus:ring-offset-alpine-gold",
  dataOid = "renovation-ribbon",
  primaryHref,
}) => {
  const { t } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  const title = t(`${translationBase}.title`);
  const description = t(`${translationBase}.description`);
  const completion = t(`${translationBase}.completion`);
  const bodyText = [description, completion].filter(Boolean).join(" ");

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || !isMounted) return;
    const hasDismissed = window.sessionStorage.getItem(storageKey);
    if (!hasDismissed) {
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 800);
      return () => clearTimeout(timer);
    }
  }, [isMounted, storageKey]);

  const handleDismiss = useCallback(() => {
    if (typeof window !== "undefined") {
      window.sessionStorage.setItem(storageKey, "true");
    }
    setIsVisible(false);
  }, [storageKey]);

  const handleLearnMore = useCallback(() => {
    setIsContactModalOpen(true);
  }, []);

  useEffect(() => {
    if (!isVisible) return;

    const timer = setTimeout(() => {
      handleDismiss();
    }, 10000);

    return () => {
      clearTimeout(timer);
    };
  }, [isVisible, handleDismiss]);

  if (!isMounted) {
    return null;
  }

  return (
    <AnimatePresence>
      {isVisible && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={handleDismiss}
            className="fixed inset-0 z-[59] bg-black/20"
            data-oid={`${dataOid}-backdrop`}
          />

          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{
              type: "spring",
              stiffness: 300,
              damping: 30,
            }}
            onClick={(e) => e.stopPropagation()}
            className={`fixed bottom-0 left-0 right-0 z-[60] w-full bg-gradient-to-r ${gradientClassName} text-white shadow-lg`}
            data-oid={dataOid}
          >
            <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
              <div className="relative flex flex-col items-center gap-3">
                <button
                  onClick={handleDismiss}
                  className={`absolute right-0 top-0 rounded-md p-1.5 text-white/90 transition-colors hover:bg-white/20 hover:text-white focus:outline-none focus:ring-2 focus:ring-white/50 focus:ring-offset-2 ${ringOffsetClassName}`}
                  aria-label={
                    primaryHref
                      ? "Dismiss"
                      : t(`${translationBase}.ctaPrimary`)
                  }
                  data-oid={`${dataOid}-dismiss`}
                >
                  <X className="h-5 w-5" />
                </button>

                <h3 className="text-center text-base font-semibold sm:text-lg">
                  {title}
                </h3>

                {bodyText ? (
                  <p className="text-center text-sm font-medium sm:text-base">
                    {bodyText}
                  </p>
                ) : null}

                <div className="flex flex-wrap items-center justify-center gap-2">
                  {primaryHref ? (
                    <Link
                      to={primaryHref}
                      onClick={handleDismiss}
                      className={`inline-flex items-center gap-1 rounded-md bg-white px-4 py-2 text-sm font-semibold text-alpine-gold transition-colors hover:bg-white/90 focus:outline-none focus:ring-2 focus:ring-white/50 focus:ring-offset-2 ${ringOffsetClassName}`}
                      data-oid={`${dataOid}-book`}
                    >
                      {t(`${translationBase}.ctaPrimary`)}
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  ) : null}

                  <button
                    onClick={handleLearnMore}
                    className={`inline-flex items-center gap-1 rounded-md bg-white/20 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-white/30 focus:outline-none focus:ring-2 focus:ring-white/50 focus:ring-offset-2 ${ringOffsetClassName}`}
                    data-oid={`${dataOid}-learn-more`}
                  >
                    {t(`${translationBase}.ctaSecondary`)}
                    <ArrowRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>

            <ContactConciergeModal
              isOpen={isContactModalOpen}
              onClose={() => setIsContactModalOpen(false)}
            />
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default RenovationRibbon;
